<template>
  <header :class="[
    'fixed top-0 left-0 w-full z-50 transition-all duration-300 py-4 px-6 md:py-6 md:px-8',
    { 'bg-white shadow-md': isScrolled || !isMdScreen, 'bg-transparent': !isScrolled && isMdScreen }
  ]">
    <div class="container mx-auto flex justify-between items-center">
      <img src="@/assets/logo.png" alt="MTS GPS" class="h-10 md:h-9 transition-all duration-300 hover:scale-105">

      <div class="flex items-center space-x-3 rtl:space-x-reverse">
        <button @click="toggleLanguage" :class="[
          'flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all duration-300 md:hidden',
          isScrolled || !isMdScreen ? 'bg-green-600 text-white hover:bg-[#75c58b]' : 'bg-white/20 text-white hover:bg-white/30'
        ]">
          <i class="fas fa-globe text-sm flex-shrink-0"></i>
          <span class="text-xs font-bold">{{ currentLocale === 'ar' ? 'EN' : 'عربي' }}</span>
        </button>

        <button @click="toggleMenu" class="md:hidden p-2 rounded-lg text-green-500 hover:bg-green-50">
          <i class="fas text-xl" :class="isOpen ? 'fa-times' : 'fa-bars'"></i>
        </button>
      </div>

      <nav :class="[
        'absolute md:static top-full left-0 w-full md:w-auto transition-all duration-300 overflow-hidden',
        isOpen ? 'max-h-[calc(100vh-80px)] opacity-100' : 'max-h-0 opacity-0 md:max-h-full md:opacity-100',
        { 'bg-white shadow-md': !isMdScreen, 'md:bg-transparent md:shadow-none': true }
      ]">
        <ul
          class="flex flex-col md:flex-row items-center py-4 md:py-0 space-y-4 md:space-y-0 md:space-x-6 lg:space-x-2 rtl:space-x-reverse font-bold">
          <li v-for="(link, index) in links" :key="index">
            <a :href="link.href" @click="setActive(link.id)" class="relative px-4 py-2 block" :class="[
              isScrolled || !isMdScreen ? 'text-[#39ab58] hover:text-green-500' : 'text-white',
              { 'nav-active': activeLink === link.id }
            ]">
              {{ $t(link.text) }}
              <span class="nav-line"></span>
            </a>
          </li>
        </ul>

        <!-- Mobile social icons and login button -->
        <div class="md:hidden flex justify-center flex-wrap gap-3 py-4">
          <!-- Login button for mobile -->
          <a href="https://mtsgps.mts.sy/"
            class="flex items-center px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 shadow-sm hover:shadow-md bg-white text-[#39ab58] border border-[#75c58b] hover:bg-green-50 cursor-pointer">
            <i class="fas fa-sign-in-alt text-lg mr-2 rtl:ml-2 rtl:mr-0"></i>
            <span class="text-sm font-bold">{{ $t('nav.login') }}</span>
          </a>

          <!-- Download app button for mobile -->
          <a :href="apkFilePath" @click.prevent="downloadAPK"
            class="flex items-center px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 shadow-sm hover:shadow-md bg-green-600 text-white hover:bg-[#75c58b] cursor-pointer">
            <i class="fas fa-download text-lg mr-2 rtl:ml-2 rtl:mr-0"></i>
            <span class="text-sm font-bold">{{ $t('footer.app') }}</span>
          </a>

          <div class="flex space-x-3 rtl:space-x-reverse">
            <a v-for="(social, index) in socials" :key="index" :href="social.link" target="_blank"
              class="w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 shadow-sm hover:shadow-md bg-green-600 text-white hover:bg-[#75c58b]">
              <i :class="social.icon" class="text-lg"></i>
            </a>
          </div>
        </div>
      </nav>

      <div class="hidden lg:flex items-center space-x-3 rtl:space-x-reverse">
        <!-- Login button for desktop -->
        <a href="https://mtsgps.mts.sy/" :class="[
          'flex items-center space-x-1 rtl:space-x-reverse px-3 py-1.5 rounded-lg transition-all duration-300',
          isScrolled ? 'bg-white text-[#39ab58] border border-[#75c58b] hover:bg-green-50' : 'bg-white/20 text-white hover:bg-white/30'
        ]">
          <i class="fas fa-sign-in-alt text-base"></i>
          <span class="text-sm font-bold">{{ $t('nav.login') }}</span>
        </a>

        <!-- Download app button for desktop -->
        <a :href="apkFilePath" @click.prevent="downloadAPK" :class="[
          'flex items-center space-x-1 rtl:space-x-reverse px-3 py-1.5 rounded-lg transition-all duration-300',
          isScrolled ? 'bg-green-600 text-white hover:bg-[#75c58b]' : 'bg-white/20 text-white hover:bg-white/30'
        ]">
          <i class="fas fa-download text-base"></i>
          <span class="text-sm font-bold">{{ $t('footer.app') }}</span>
        </a>

        <div class="flex space-x-2 rtl:space-x-reverse">
          <a v-for="(social, index) in socials" :key="index" :href="social.link" target="_blank" :class="[
            'w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 shadow-sm hover:shadow-md',
            isScrolled ? 'bg-green-600 text-white hover:bg-[#75c58b]' : 'bg-white/20 text-white hover:bg-white/30'
          ]">
            <i :class="social.icon" class="text-base"></i>
          </a>
        </div>

        <!-- Language toggle button for desktop -->
        <button @click="toggleLanguage" :class="[
          'flex items-center space-x-1 rtl:space-x-reverse px-3 py-1.5 rounded-lg transition-all duration-300',
          isScrolled ? 'bg-green-600 text-white hover:bg-[#75c58b]' : 'bg-white/20 text-white hover:bg-white/30'
        ]">
          <i class="fas fa-globe text-base"></i>
          <span class="text-sm font-bold">{{ currentLocale === 'ar' ? 'EN' : 'عربي' }}</span>
        </button>

      </div>
    </div>
  </header>
</template>

<script>
import { useI18n } from 'vue-i18n'

export default {
  name: "NavigationBar",
  data() {
    return {
      isOpen: false,
      isScrolled: false,
      isMdScreen: false,
      activeLink: 'home',
      links: [
        { text: 'nav.home', href: '#hero', id: 'home' },
        { text: 'nav.services', href: '#services', id: 'services' },
        { text: 'nav.pricing', href: '#pricing', id: 'pricing' },
        { text: 'nav.about', href: '#about', id: 'about' },
        { text: 'nav.contact', href: '#contact', id: 'contact' }
      ],
      socials: [
        { icon: 'fas fa-envelope', link: 'mailto:info@mtsgps.com' },
        { icon: 'fab fa-instagram', link: 'https://www.instagram.com/mts_gps/' },
        { icon: 'fab fa-facebook', link: 'https://www.facebook.com/mtsgps1' }
      ],
      apkFileName: 'mts_gps_2.4.3.apk',
      apkFilePath: '/downloads/mts_gps_2.4.3.apk'
    }
  },
  setup() {
    const { locale } = useI18n()

    // Get saved language from localStorage or default to 'en'
    const savedLang = localStorage.getItem('lang') || 'en'

    // Set initial locale
    locale.value = savedLang

    // Set initial direction
    document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr'

    return {
      currentLocale: locale
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.checkActiveSection);
    this.handleResize();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.checkActiveSection);
  },
  methods: {
    downloadAPK() {
      try {
        // إنشاء عنصر رابط للتنزيل
        const link = document.createElement('a');
        link.href = this.apkFilePath;
        link.setAttribute('download', this.apkFileName);

        // إضافة عنصر الرابط للصفحة
        document.body.appendChild(link);

        // النقر على الرابط لبدء التنزيل
        link.click();

        // إزالة عنصر الرابط من الصفحة بعد التنزيل
        document.body.removeChild(link);

        console.log('بدء تنزيل: ' + this.apkFileName);
      } catch (error) {
        console.error('حدث خطأ أثناء تنزيل الملف:', error);
      }
    },
    toggleLanguage() {
      const newLocale = this.currentLocale === 'ar' ? 'en' : 'ar'
      this.currentLocale = newLocale
      localStorage.setItem('lang', newLocale)
      document.documentElement.dir = newLocale === 'ar' ? 'rtl' : 'ltr'
    },
    toggleMenu() {
      this.isOpen = !this.isOpen;
    },
    closeMenu() {
      this.isOpen = false;
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    handleResize() {
      this.isMdScreen = window.innerWidth >= 768;
    },
    setActive(id) {
      this.activeLink = id;
      this.closeMenu();
    },
    checkActiveSection() {
      const sections = this.links.map(link => ({
        id: link.id,
        element: document.querySelector(link.href)
      }));

      const viewportMiddle = window.innerHeight / 2;

      for (const section of sections) {
        if (!section.element) continue;

        const rect = section.element.getBoundingClientRect();
        if (rect.top <= viewportMiddle && rect.bottom >= viewportMiddle) {
          this.activeLink = section.id;
          break;
        }
      }
    }
  }
}
</script>

<style scoped>
/* بقية الستايل كما هو */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&display=swap');

/* إضافة دعم للغة العربية */
[dir="rtl"] .space-x-2> :not([hidden])~ :not([hidden]),
[dir="rtl"] .space-x-3> :not([hidden])~ :not([hidden]),
[dir="rtl"] .space-x-4> :not([hidden])~ :not([hidden]),
[dir="rtl"] .space-x-6> :not([hidden])~ :not([hidden]),
[dir="rtl"] .space-x-10> :not([hidden])~ :not([hidden]) {
  --tw-space-x-reverse: 1;
}

header {
  font-family: 'Poppins', sans-serif;
}

[dir="rtl"] header {
  font-family: 'Cairo', sans-serif;
}

.nav-line {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: currentColor;
  transform: translateX(-50%);
  transition: width 0.3s ease;
}

a:hover .nav-line {
  width: 80%;
}

.nav-active .nav-line {
  width: 80%;
}

nav {
  transition: max-height 0.3s ease-out, opacity 0.2s ease-out, background-color 0.3s ease;
}

img {
  transform-origin: left center;
}

[dir="rtl"] img {
  transform-origin: right center;
}

@media (max-width: 1024px) {
  .md\\:hidden {
    display: block;
  }

  .md\\:flex {
    display: none;
  }
}

@media (min-width: 1024px) {
  .md\\:hidden {
    display: none;
  }

  .md\\:flex {
    display: flex;
  }
}
</style>
